import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  SSTFApiClient,
  Workspace,
  Project,
  TestRun,
  FileMetadata,
  OperationLog,
  UpdateTestResultsInput,
  ProjectStatusIds,
  extractProjectStatusIds,
  Template,
  TestStepStatusIds,
  extractTestStepStatusIds,
  TestTarget,
} from "../services/api";
import {
  ApiTestResult,
  SearchFilters,
  transformApiTestResults,
  calculateTestResultStats,
  sortTestResultsByPriority,
  sortTestResultsByDisplayOrder,
  deduplicateLatestResults,
} from "../utils/dataTransform";
import { apiTestResultHasHumanAiJudgmentMismatch } from "../utils/judgmentUtils";

// presigned URL 取得の同時実行上限（バルク API が使えない場合のフォールバック用）。
// SSTF 側のレートリミットとブラウザの同一ホスト同時接続数を考慮。
const FETCH_CONCURRENCY = 8;

/**
 * 複数の file_id について presigned URL を取得する。
 * まず BFF のバルクエンドポイント (`POST /files/bulk-signed-urls`) で
 * 1 リクエストにまとめて取得し、未デプロイ等で失敗した場合は
 * 並列フォールバック（旧仕様の N リクエスト並列）に切り替える。
 *
 * onResult は URL が解決できた key 単位で呼ばれる（プログレッシブ更新用）。
 * 個別の失敗は握り潰して全体は止めない（既存挙動を踏襲）。
 */
async function fetchUrlsBulk(
  jobs: Array<{ key: string; fileId: string }>,
  apiClient: SSTFApiClient,
  workspaceId: string,
  projectId: string,
  onResult: (key: string, url: string) => void
): Promise<void> {
  if (jobs.length === 0) return;

  // 1) バルク API を試す
  const uniqueIds = Array.from(new Set(jobs.map((j) => j.fileId)));
  try {
    const idToUrl = await apiClient.bulkGetFileDownloadUrls(
      workspaceId,
      projectId,
      uniqueIds
    );
    for (const job of jobs) {
      const url = idToUrl[job.fileId];
      if (url) onResult(job.key, url);
    }
    // バルクで全件取れた、または部分成功で取れた分は反映済み。
    // 取れなかった file_id について個別取得はしない（バルク API が空 URL を返した
    // のはバックエンド側の判断＝当該 fileId に signed URL を発行できない、
    // と捉えるのが妥当）。
    return;
  } catch (err) {
    console.warn("bulk signed URL 取得に失敗、個別取得にフォールバック:", err);
  }

  // 2) フォールバック: 旧仕様の並列個別取得
  let cursor = 0;
  const workerCount = Math.min(FETCH_CONCURRENCY, jobs.length);
  const workers: Promise<void>[] = [];
  for (let i = 0; i < workerCount; i++) {
    workers.push(
      (async () => {
        while (true) {
          const idx = cursor++;
          if (idx >= jobs.length) return;
          const { key, fileId } = jobs[idx];
          try {
            const url = await apiClient.getFileDownloadUrlForDisplay(
              workspaceId,
              projectId,
              fileId
            );
            if (url) onResult(key, url);
          } catch (err) {
            console.error(`エビデンスファイル ${key} の取得に失敗:`, err);
          }
        }
      })()
    );
  }
  await Promise.all(workers);
}

// フックの戻り値の型定義
export interface UseTestResultsReturn {
  // データ
  testResults: ApiTestResult[];
  workspaces: Workspace[];
  projects: Project[];
  testRuns: TestRun[];
  selectedResultFiles: FileMetadata[];
  operationLog: OperationLog | null;
  evidenceFiles: Record<string, string>; // ファイル名 -> URL のマップ
  projectStatusIds: ProjectStatusIds | null; // プロジェクト固有のステータスID
  testStepStatusIds: TestStepStatusIds | null; // テストステップ固有のステータスID
  template: Template | null; // プロジェクトテンプレート
  testStepTemplate: Template | null; // テストステップテンプレート
  testTargets: TestTarget[]; // テストターゲット一覧（マスク情報 lookup 用）
  loadingTestTargets: boolean;

  // 統計情報
  stats: {
    total: number;
    completed: number;
    statusCounts: Record<string, number>;
  };

  // 状態
  loading: boolean;
  error: string | null;
  loadingFiles: boolean;
  loadingOperationLog: boolean;
  loadingEvidenceFiles: boolean;
  updatingTestResult: boolean;
  loadingTemplate: boolean;

  // アクション
  searchTestResults: (filters: SearchFilters) => Promise<void>;
  loadWorkspaces: () => Promise<void>;
  loadProjects: (workspaceId: string) => Promise<void>;
  loadTestRuns: (workspaceId: string, projectId: string) => Promise<void>;
  loadProjectTemplate: (
    workspaceId: string,
    projectId: string
  ) => Promise<void>;
  loadFileMetadata: (
    workspaceId: string,
    projectId: string,
    fileIds: string[]
  ) => Promise<void>;
  loadOperationLogAndEvidences: (
    workspaceId: string,
    projectId: string,
    files: FileMetadata[],
    testCaseId?: string
  ) => Promise<void>;
  resetSelectedEvidence: () => void;
  markAsProcessed: (resultId: string) => void;
  clearError: () => void;
  updateTestResult: (
    workspaceId: string,
    projectId: string,
    testRunId: string,
    resultId: string,
    scriptTestRunMatrixId: string,
    testResultStatusId: string,
    actualTimeInSeconds?: number,
    currentValue?: string
  ) => Promise<void>;
  updateTestResultValue: (resultId: number, value: any) => Promise<void>;

  // フィルタリング
  filteredResults: ApiTestResult[];
  applyFilters: (filters: Partial<SearchFilters>) => void;

  // 単一テスト結果取得
  fetchSingleResult: (
    workspaceId: string,
    projectId: string,
    resultId: string
  ) => Promise<import("../services/api").ScriptTestResultDetail | null>;
}

// フックの実装
export function useTestResults(
  apiClient: SSTFApiClient | null
): UseTestResultsReturn {
  // 状態管理
  const [testResults, setTestResults] = useState<ApiTestResult[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [testTargets, setTestTargets] = useState<TestTarget[]>([]);
  const [loadingTestTargets, setLoadingTestTargets] = useState(false);
  const [selectedResultFiles, setSelectedResultFiles] = useState<
    FileMetadata[]
  >([]);
  const [operationLog, setOperationLog] = useState<OperationLog | null>(null);
  const [evidenceFiles, setEvidenceFiles] = useState<Record<string, string>>(
    {} // ファイル名 -> URL のマップ
  );
  const [currentTestCaseId, setCurrentTestCaseId] = useState<string | null>(
    null
  ); // 現在のテストケースID
  // テスト結果切替時の evidence/operationLog ロード競合対策 (#433)。
  // 結果切替直後は前結果のファイルメタデータで先に load が走り、続いて正しい
  // ファイルで load が走る。両者が同じキー(primary_evidence.webm 等)へ書き込むため、
  // 古い(先発)呼び出しの応答が後着すると前の録画が残ってしまう。
  // 呼び出しごとに連番を振り、「最新の load の結果だけ」を state へ反映する。
  const loadSeqRef = useRef(0);
  // テスト結果の選択世代。結果を切り替えるたび(resetSelectedEvidence)に ++ する。
  // loadFileMetadata は開始時の世代を覚え、getFilesMetadata 応答が後着しても
  // 選択が変わっていれば selectedResultFiles を上書きしない(stale 応答ガード)。
  // ※ loadOperationLogAndEvidences 用の loadSeqRef とは別管理。あちらは
  //   loadOperationLogAndEvidences 内で ++ されるため、ファイルメタの選択世代に
  //   流用すると干渉してしまう。
  const selectionSeqRef = useRef(0);
  const [projectStatusIds, setProjectStatusIds] =
    useState<ProjectStatusIds | null>(null);
  const [testStepStatusIds, setTestStepStatusIds] =
    useState<TestStepStatusIds | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [testStepTemplate, setTestStepTemplate] = useState<Template | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingOperationLog, setLoadingOperationLog] = useState(false);
  const [loadingEvidenceFiles, setLoadingEvidenceFiles] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [updatingTestResult, setUpdatingTestResult] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<Partial<SearchFilters>>(
    {}
  );

  // 統計情報の計算
  const stats = calculateTestResultStats(testResults);

  // フィルタリングされた結果
  // useMemo で配列の参照を安定化することで、下流の useEffect が
  // 毎レンダーで再発火するのを防ぐ。
  const filteredResults = useMemo(
    () =>
      testResults.filter((result) => {
        if (currentFilters.searchType === "analyzer_waiting") {
          // AI関連の全ステータスIDでフィルタリング
          const isAnalyzerWaiting = [
            "25cf3b1c-beae-56a5-af98-484bfa54577c", // [AI]OK
            "8b98a0b2-4127-5c19-b4c7-0515161b76bc", // [AI]Result NG
            "c73fe336-6105-55aa-9b3d-f749bf0f4572", // [AI]Procedure NG
            "ba892ddf-e74f-5981-bfe5-a32b40b035c3", // [AI]OK(Need Review)
            "c0cdc5c4-adb6-5702-8e99-9835f25f3ebb", // [AI]Result NG(Need Review)
            "c44cb80d-a379-5163-8382-03bda0724a18", // [AI]Procedure NG(Need Review)
          ].includes(result.scriptTestRunResult.test_result_status_id);
          if (!isAnalyzerWaiting) return false;
        } else if (currentFilters.searchType === "interrupted") {
          // 途中終了のフィルタリング - ステータスIDで判定
          const isInterrupted = [
            "interrupted_status_id", // 実際のステータスIDに置き換える
            "error_status_id", // 実際のステータスIDに置き換える
          ].includes(result.scriptTestRunResult.test_result_status_id);
          if (!isInterrupted) return false;
        } else if (currentFilters.searchType === "needs_review") {
          // 要確認ステータスのみでフィルタリング
          const isNeedsReview = [
            "ba892ddf-e74f-5981-bfe5-a32b40b035c3", // [AI]OK(Need Review)
            "c0cdc5c4-adb6-5702-8e99-9835f25f3ebb", // [AI]Result NG(Need Review)
            "c44cb80d-a379-5163-8382-03bda0724a18", // [AI]Procedure NG(Need Review)
          ].includes(result.scriptTestRunResult.test_result_status_id);
          if (!isNeedsReview) return false;
        }

        if (currentFilters.judgmentMismatchOnly) {
          return apiTestResultHasHumanAiJudgmentMismatch(result);
        }
        return true;
      }),
    [testResults, currentFilters.searchType, currentFilters.judgmentMismatchOnly]
  );

  // エラークリア
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // テスト結果更新
  const updateTestResult = useCallback(
    async (
      workspaceId: string,
      projectId: string,
      testRunId: string,
      resultId: string,
      scriptTestRunMatrixId: string,
      testResultStatusId: string,
      actualTimeInSeconds?: number,
      currentValue?: string
    ) => {
      if (!apiClient) {
        setError("APIクライアントが設定されていません");
        return;
      }

      setUpdatingTestResult(true);
      setError(null);

      try {
        // 既存のvalueを取得してマージ
        console.log("デバッグ: updateTestResult呼び出し", {
          resultId,
          currentValue,
          testResultsLength: testResults.length,
        });

        let existingValue = {};

        // currentValueが渡されている場合はそれを使用
        if (currentValue) {
          try {
            existingValue = JSON.parse(currentValue);
            console.log("currentValueから既存のvalueを取得:", existingValue);
          } catch (error) {
            console.warn("currentValueのパースに失敗:", error);
            existingValue = {};
          }
        } else {
          // フォールバック: testResultsから検索
          const currentResult = testResults.find(
            (result) => result.scriptTestRunResult.result_id === resultId
          );

          console.log("testResultsからcurrentResultを検索:", currentResult);
          if (currentResult?.scriptTestRunResult.value) {
            try {
              existingValue = JSON.parse(
                currentResult.scriptTestRunResult.value
              );
              console.log("testResultsから既存のvalueを取得:", existingValue);
            } catch (error) {
              console.warn("既存のvalueのパースに失敗:", error);
              existingValue = {};
            }
          } else {
            console.warn("currentResultまたはvalueが見つかりません");
          }
        }

        // プロジェクト固有のAIステータスIDを使用
        const aiCompletedStatusId =
          projectStatusIds?.aiStatus.COMPLETED ||
          "c70bf71d-19ad-5abe-b126-2426dee1af4f"; // フォールバック値

        // 既存のvalueと新しい値をマージ
        const mergedValue = {
          ...existingValue,
          test_result_ai_status: aiCompletedStatusId,
        };

        const input: UpdateTestResultsInput = {
          workspaceId,
          projectId,
          testRunId,
          testResultInputs: [
            {
              resultId,
              scriptTestRunMatrixId,
              testResultStatusId, // これは実際のテスト結果ステータス
              actualTimeInSeconds,
              value: JSON.stringify(mergedValue),
            },
          ],
        };

        console.log("テスト結果更新開始:", input);
        const response = await apiClient.updateTestResults(input);
        console.log("テスト結果更新完了:", response);

        // ローカルのテスト結果を更新
        setTestResults((prevResults) =>
          prevResults.map((result) => {
            if (result.scriptTestRunResult.result_id === resultId) {
              return {
                ...result,
                scriptTestRunResult: {
                  ...result.scriptTestRunResult,
                  test_result_status_id: testResultStatusId,
                  modified_at: new Date().toISOString(),
                },
              };
            }
            return result;
          })
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "テスト結果の更新に失敗しました";
        setError(errorMessage);
        console.error("テスト結果更新エラー:", err);
        throw err; // 呼び出し元にエラーを再スロー
      } finally {
        setUpdatingTestResult(false);
      }
    },
    [apiClient, testResults, projectStatusIds]
  );

  // ワークスペース読み込み
  const loadWorkspaces = useCallback(async () => {
    if (!apiClient) {
      setError("APIクライアントが設定されていません");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const workspaceList = await apiClient.getWorkspaces(true);
      setWorkspaces(workspaceList);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "ワークスペースの取得に失敗しました";
      setError(errorMessage);
      console.error("ワークスペース取得エラー:", err);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  // プロジェクト読み込み
  const loadProjects = useCallback(
    async (workspaceId: string) => {
      if (!apiClient) {
        setError("APIクライアントが設定されていません");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const projectList = await apiClient.getProjects(workspaceId, true);
        setProjects(projectList);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "プロジェクトの取得に失敗しました";
        setError(errorMessage);
        console.error("プロジェクト取得エラー:", err);
      } finally {
        setLoading(false);
      }
    },
    [apiClient]
  );

  // テストラン読み込み
  const loadTestRuns = useCallback(
    async (workspaceId: string, projectId: string) => {
      if (!apiClient) {
        setError("APIクライアントが設定されていません");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log("テストランを読み込み中...", { workspaceId, projectId });
        const testRunList = await apiClient.getTestRuns(workspaceId, projectId);
        setTestRuns(testRunList);
        console.log("テストランを読み込みました:", testRunList.length);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "テストランの取得に失敗しました";
        setError(errorMessage);
        console.error("テストラン取得エラー:", err);
      } finally {
        setLoading(false);
      }
    },
    [apiClient]
  );

  // プロジェクトテンプレート読み込み
  const loadProjectTemplate = useCallback(
    async (workspaceId: string, projectId: string) => {
      if (!apiClient) {
        setError("APIクライアントが設定されていません");
        return;
      }

      setLoadingTemplate(true);
      setError(null);

      try {
        console.log("プロジェクトテンプレートを取得中...", {
          workspaceId,
          projectId,
        });

        // テストケース用テンプレートを取得
        console.log("=== テストケース用テンプレート取得開始 ===");
        const template = await apiClient.getTemplate(
          "test_result",
          workspaceId,
          projectId
        );
        const statusIds = extractProjectStatusIds(template);
        setProjectStatusIds(statusIds);
        setTemplate(template);
        console.log("テストケース用ステータスIDを取得しました:", statusIds);

        // テストステップ用テンプレートを取得
        console.log("=== テストステップ用テンプレート取得開始 ===");

        try {
          console.log("teststep_resultテンプレートを取得中...");

          // テストステップ専用のテンプレートを取得
          const testStepTemplate = await apiClient.getTemplate(
            "teststep_result",
            workspaceId,
            projectId
          );

          // テストステップ用ステータスIDを抽出
          const testStepStatusIds = extractTestStepStatusIds(testStepTemplate);
          setTestStepStatusIds(testStepStatusIds);
          setTestStepTemplate(testStepTemplate);
          console.log(
            "✅ テストステップ用テンプレートとステータスIDを取得しました:",
            {
              template: testStepTemplate,
              statusIds: testStepStatusIds,
            }
          );
        } catch (testStepError) {
          console.warn(
            "❌ teststep_resultテンプレートの取得に失敗、フォールバックを試行:",
            testStepError
          );

          // フォールバック1: test_resultテンプレートからテストステップ用ステータスIDを抽出
          try {
            console.log(
              "test_resultテンプレートからテストステップ用ステータスIDを抽出中..."
            );
            const testStepStatusIds = extractTestStepStatusIds(template);
            setTestStepStatusIds(testStepStatusIds);
            setTestStepTemplate(template); // 同じテンプレートを使用
            console.log(
              "✅ test_resultテンプレートからテストステップ用ステータスIDを取得しました:",
              testStepStatusIds
            );
          } catch (fallbackError) {
            console.error(
              "❌ test_resultテンプレートからの抽出も失敗:",
              fallbackError
            );

            // フォールバック2: ハードコードされた値を使用
            const fallbackStatusIds = {
              OK: "bf525e4e-24f6-56c2-a365-ded4a2a2beae",
              NG: "458cb677-0708-5af4-b926-bb7d398db6bb",
              SUCCESS_IDS: [
                "bf525e4e-24f6-56c2-a365-ded4a2a2beae", // OK
                "97f58964-0806-5b41-9171-6cb0d07fa1a7", // NG→OK
                "0aa084ea-af4c-5173-bebd-bf241bc30529", // [AI]OK
              ],
              WARNING_IDS: [
                "674ff3fe-81dd-5f86-9c72-3280cf3e5838", // [AI]OK(Need Review)
                "53bb2e13-d5a0-514b-a4fe-e8c938f49cb9", // [AI]Result NG(Need Review)
                "36208def-29dd-5a23-924a-4467c6f2e656", // [AI]Procedure NG(Need Review)
              ],
            };
            setTestStepStatusIds(fallbackStatusIds);
            setTestStepTemplate(null);
            console.log("最終フォールバック値を使用:", fallbackStatusIds);
          }
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "テンプレートの取得に失敗しました";
        setError(errorMessage);
        console.error("テンプレート取得エラー:", err);
      } finally {
        setLoadingTemplate(false);
      }
    },
    [apiClient]
  );

  // テスト結果検索
  const searchTestResults = useCallback(
    async (filters: SearchFilters) => {
      if (!apiClient) {
        setError("APIクライアントが設定されていません");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // 検索クエリの構築
        const query: any = {
          or: [],
          and: [],
        };

        // テストランIDが指定されている場合（andクエリとして追加）
        if (filters.testRunId) {
          query.and.push({
            key: "_meta.test_runs.id",
            type: "keyword",
            value: filters.testRunId,
          });
        }

        // 検索タイプに応じたフィルタリング（orクエリとして追加）
        if (filters.searchType === "analyzer_waiting") {
          // AI関連の全ステータスIDを対象にする
          query.or.push(
            {
              key: "test_result_status_id",
              type: "exact",
              value: "25cf3b1c-beae-56a5-af98-484bfa54577c", // [AI]OK
            },
            {
              key: "test_result_status_id",
              type: "exact",
              value: "8b98a0b2-4127-5c19-b4c7-0515161b76bc", // [AI]Result NG
            },
            {
              key: "test_result_status_id",
              type: "exact",
              value: "c73fe336-6105-55aa-9b3d-f749bf0f4572", // [AI]Procedure NG
            },
            {
              key: "test_result_status_id",
              type: "exact",
              value: "ba892ddf-e74f-5981-bfe5-a32b40b035c3", // [AI]OK(Need Review)
            },
            {
              key: "test_result_status_id",
              type: "exact",
              value: "c0cdc5c4-adb6-5702-8e99-9835f25f3ebb", // [AI]Result NG(Need Review)
            },
            {
              key: "test_result_status_id",
              type: "exact",
              value: "c44cb80d-a379-5163-8382-03bda0724a18", // [AI]Procedure NG(Need Review)
            }
          );
        } else if (filters.searchType === "interrupted") {
          // 途中終了の条件（複数のステータスIDをORで繋ぐ）
          query.or.push(
            {
              key: "test_result_status_id",
              type: "exact",
              value: "interrupted_status_id", // 実際のステータスIDに置き換える
            },
            {
              key: "test_result_status_id",
              type: "exact",
              value: "error_status_id", // 実際のステータスIDに置き換える
            }
          );
        } else if (filters.searchType === "needs_review") {
          // 要確認ステータスのみを対象にする
          query.or.push(
            {
              key: "test_result_status_id",
              type: "exact",
              value: "ba892ddf-e74f-5981-bfe5-a32b40b035c3", // [AI]OK(Need Review)
            },
            {
              key: "test_result_status_id",
              type: "exact",
              value: "c0cdc5c4-adb6-5702-8e99-9835f25f3ebb", // [AI]Result NG(Need Review)
            },
            {
              key: "test_result_status_id",
              type: "exact",
              value: "c44cb80d-a379-5163-8382-03bda0724a18", // [AI]Procedure NG(Need Review)
            }
          );
        }

        // 追加のクエリが指定されている場合はそれを使用
        let queryParam: any = filters.query || undefined;
        
        // 既存のクエリ条件がある場合は追加
        if (!queryParam && (query.or.length > 0 || query.and.length > 0)) {
          queryParam = {};
          if (query.or.length > 0) {
            queryParam.or = query.or;
          }
          if (query.and.length > 0) {
            queryParam.and = query.and;
          }
        }

        console.log("検索クエリ:", queryParam);

        // ページング処理で全件取得
        let allApiResults: any[] = [];
        let page = 1;
        const pageSize = 1000;
        let hasMore = true;
        
        while (hasMore) {
          const response = await apiClient.findTestResults({
            page: page,
            size: pageSize,
            workspace_id: filters.workspace,
            project_id: filters.project,
            query: queryParam,
          });
          
          if (response?.script_test_result_details && response.script_test_result_details.length > 0) {
            allApiResults = allApiResults.concat(response.script_test_result_details);
            
            if (response.script_test_result_details.length < pageSize) {
              // ページサイズ未満の結果が返ってきた場合は最終ページ
              hasMore = false;
            } else {
              page++;
            }
          } else {
            hasMore = false;
          }
        }
        
        console.log(`全${allApiResults.length}件のデータを取得`);
        
        // 同一 (test_run, testcase, env) で最新 result のみに絞り込む。
        // `keepResultIds` で明示指定された result は古くても残す（ダイレクトモード
        // で URL 指定された過去 result が dedup で消えないようにする）。
        const latestResultsArray = deduplicateLatestResults(
          allApiResults,
          filters.keepResultIds
        );
        console.log(`最新の結果のみ: ${latestResultsArray.length}件`);

        // ワークスペースとプロジェクトのスラッグマップを作成
        const workspaceSlugMap: Record<string, string> = {};
        const projectSlugMap: Record<string, string> = {};

        workspaces.forEach((ws) => {
          workspaceSlugMap[ws.workspace_id] = ws.slug;
        });

        projects.forEach((proj) => {
          projectSlugMap[proj.project_id] = proj.slug;
        });

        // データ変換
        console.log("APIレスポンス:", latestResultsArray.length, "件");
        console.log("ワークスペースマップ:", workspaceSlugMap);
        console.log("プロジェクトマップ:", projectSlugMap);

        const transformedResults = transformApiTestResults(
          latestResultsArray,
          workspaceSlugMap,
          projectSlugMap,
          "https://bff.dev.sstf.agest.app", // 実際のSSTF URLに置き換える(この値は利用されない)
          template || undefined
        );

        console.log("変換後の結果:", transformedResults);

        // display_order順でソート
        const sortedResults = sortTestResultsByDisplayOrder(transformedResults);

        console.log("ソート後の結果:", sortedResults);

        setTestResults(sortedResults);
        setCurrentFilters(filters);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "テスト結果の検索に失敗しました";
        setError(errorMessage);
        console.error("テスト結果検索エラー:", err);
      } finally {
        setLoading(false);
      }
    },
    [apiClient, workspaces, projects, template]
  );

  // 単一テスト結果取得（ダイレクトモード用）
  const fetchSingleResult = useCallback(
    async (workspaceId: string, projectId: string, resultId: string) => {
      if (!apiClient) throw new Error("APIクライアントが設定されていません");
      const response = await apiClient.findTestResults({
        page: 1,
        size: 1,
        workspace_id: workspaceId,
        project_id: projectId,
        query: { and: [{ key: "id", type: "eq", value: resultId }] },
      });
      return response?.script_test_result_details?.[0] || null;
    },
    [apiClient]
  );

  // 処理済みマーク
  const markAsProcessed = useCallback((resultId: string) => {
    setTestResults((prev) =>
      prev.map((result) =>
        result.scriptTestRunResult.result_id === resultId
          ? { ...result, isProcessed: true }
          : result
      )
    );
  }, []);

  // フィルタ適用
  const applyFilters = useCallback((filters: Partial<SearchFilters>) => {
    setCurrentFilters((prev) => ({ ...prev, ...filters }));
  }, []);

  // ファイルメタデータ読み込み
  const loadFileMetadata = useCallback(
    async (workspaceId: string, projectId: string, fileIds: string[]) => {
      console.log("loadFileMetadata呼び出し:", {
        workspaceId,
        projectId,
        fileIds,
      });

      if (!apiClient) {
        setError("APIクライアントが設定されていません");
        return;
      }

      // この取得が始まった時点の選択世代。応答が後着しても、選択が切り替わっていたら
      // selectedResultFiles を上書きしない (#433: 古い結果のファイルで別結果の動画が出る競合を防ぐ)。
      const mySelection = selectionSeqRef.current;
      const isStaleSelection = () => mySelection !== selectionSeqRef.current;

      if (fileIds.length === 0) {
        if (!isStaleSelection()) setSelectedResultFiles([]);
        return;
      }

      setLoadingFiles(true);
      setError(null);

      try {
        console.log("ファイルメタデータを取得中...");
        const filesMetadata = await apiClient.getFilesMetadata(
          workspaceId,
          projectId,
          fileIds
        );
        console.log("ファイルメタデータ取得完了:", filesMetadata);
        if (!isStaleSelection()) setSelectedResultFiles(filesMetadata);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "ファイルメタデータの取得に失敗しました";
        console.error("ファイルメタデータ取得エラー:", err);
        // 既に別結果へ切り替わっていたら、もう選択していない結果の失敗を
        // 現在の結果のエラーとして表面化させない (#433: stale なエラーダイアログ抑止)。
        if (!isStaleSelection()) setError(errorMessage);
      } finally {
        // 進行中の最新リクエストのローディング表示を、stale な完了で消さない。
        if (!isStaleSelection()) setLoadingFiles(false);
      }
    },
    [apiClient]
  );

  // エビデンスファイルを直接ファイル名から取得するヘルパー関数
  const loadEvidenceFilesDirectly = useCallback(
    async (
      workspaceId: string,
      projectId: string,
      files: FileMetadata[],
      seq?: number
    ) => {
      if (!apiClient) return;
      // 最新の load 以外（古い呼び出し）の結果は反映しない (#433)
      const isStale = () => seq !== undefined && seq !== loadSeqRef.current;

      console.log("ファイル名パターンベースでエビデンスファイルを取得開始");
      setLoadingEvidenceFiles(true);

      // 画像キャプチャとエビデンス動画のパターンにマッチするファイルを検索
      const evidenceFiles = files.filter((file) => {
        const fileName = file.file_name;
        return (
          /expected_step_\d+\.(jpg|jpeg|png)/i.test(fileName) ||
          /evidence_\d+\.(webm|mp4|mov)/i.test(fileName) ||
          /\[ai_generated\]primary_evidence\.(webm|mp4|mov)/i.test(fileName)
        );
      });

      console.log(
        "パターンマッチしたエビデンスファイル:",
        evidenceFiles.map((f) => f.file_name)
      );

      // 並列取得し、取れた順に setEvidenceFiles へマージしてプログレッシブ表示する。
      await fetchUrlsBulk(
        evidenceFiles.map((f) => ({ key: f.file_name, fileId: f.file_id })),
        apiClient,
        workspaceId,
        projectId,
        (key, url) => {
          if (isStale()) return;
          setEvidenceFiles((prev) => ({ ...prev, [key]: url }));
        }
      );

      console.log("全エビデンスファイルのURL取得完了");
      if (!isStale()) setLoadingEvidenceFiles(false);
    },
    [apiClient]
  );

  // 指定されたファイル名のエビデンスファイルを取得するヘルパー関数
  const loadEvidenceFilesByNames = useCallback(
    async (
      workspaceId: string,
      projectId: string,
      files: FileMetadata[],
      fileNames: string[],
      seq?: number
    ) => {
      if (!apiClient) return;
      // 最新の load 以外（古い呼び出し）の結果は反映しない (#433)
      const isStale = () => seq !== undefined && seq !== loadSeqRef.current;

      console.log("エビデンスファイルのダウンロードURLを取得開始");
      setLoadingEvidenceFiles(true);

      // ファイル名 → file_id を解決し、見つからないものは警告を出してスキップ
      const fileNameToId = new Map<string, string>();
      for (const f of files) fileNameToId.set(f.file_name, f.file_id);

      const jobs: { key: string; fileId: string }[] = [];
      for (const fileName of fileNames) {
        const fileId = fileNameToId.get(fileName);
        if (fileId) {
          jobs.push({ key: fileName, fileId });
        } else {
          console.warn(
            `エビデンスファイル ${fileName} がファイルリストに見つかりません`
          );
        }
      }

      // 並列取得＋プログレッシブ更新。逐次 await ループは廃止。
      await fetchUrlsBulk(
        jobs,
        apiClient,
        workspaceId,
        projectId,
        (key, url) => {
          if (isStale()) return;
          setEvidenceFiles((prev) => ({ ...prev, [key]: url }));
        }
      );

      console.log("全エビデンスファイルのURL取得完了");
      if (!isStale()) setLoadingEvidenceFiles(false);
    },
    [apiClient]
  );

  // 操作ログとエビデンスファイルを読み込み
  const loadOperationLogAndEvidences = useCallback(
    async (
      workspaceId: string,
      projectId: string,
      files: FileMetadata[],
      testCaseId?: string
    ) => {
      console.log("loadOperationLogAndEvidences呼び出し:", {
        workspaceId,
        projectId,
        files,
        testCaseId,
      });

      if (!apiClient) {
        setError("APIクライアントが設定されていません");
        return;
      }

      // この load 呼び出しの連番。以降の async 応答は最新の連番のときだけ反映する (#433)
      const mySeq = ++loadSeqRef.current;
      const isStale = () => mySeq !== loadSeqRef.current;

      // テストケースが変更された場合のみエビデンスファイルをクリア
      if (testCaseId && testCaseId !== currentTestCaseId) {
        console.log(
          `テストケース変更を検出: ${currentTestCaseId} -> ${testCaseId}`
        );
        setCurrentTestCaseId(testCaseId);
        setOperationLog(null);
        setEvidenceFiles({});
      }

      setLoadingOperationLog(true);
      setLoadingEvidenceFiles(true);
      setError(null);

      try {
        // 操作ログファイルを見つける
        console.log(
          "操作ログファイルを検索中:",
          files.map((f) => f.file_name)
        );
        const operationLogFile = files.find((file) =>
          file.file_name.includes("autotest_log.json")
        );

        // 操作ログJSONのダウンロード
        // ここで取得対象のファイル名一覧を作る。loadingOperationLog は
        // 「操作ログ JSON 自体の取得中」の意味だけに絞り、
        // 個別エビデンス URL の取得は loadingEvidenceFiles で別管理する
        // （プログレッシブ表示のため）。
        let evidenceFileNames: string[] | null = null;

        if (operationLogFile) {
          try {
            console.log(
              `操作ログファイルをダウンロード中: ${operationLogFile.file_name} (ID: ${operationLogFile.file_id})`
            );

            const log = await apiClient.downloadOperationLog(
              workspaceId,
              projectId,
              operationLogFile.file_id
            );

            console.log("操作ログダウンロード完了:", log);
            if (!isStale()) setOperationLog(log);

            // 操作ログからエビデンスファイル名を抽出
            const names = new Set<string>();

            // ステップからエビデンスファイル名を収集
            log.steps.forEach((step) => {
              if (step.evidence) names.add(step.evidence);
            });

            // 動画ファイル名も収集
            log.videos.forEach((video) => {
              names.add(video.file);
            });

            // 解析後生成物（diff_step_*.png, expected_step_*_target.jpg）も収集。
            // autotest_log の step.evidence には載らないため、managed_files から直接拾う。
            // primary_evidence.webm も log.videos に無い場合に備えて拾っておく。
            files.forEach((f) => {
              if (
                /^\[ai_generated\]diff_step_\d+(_target)?\.png$/.test(f.file_name) ||
                /^\[ai_generated\]expected_step_\d+_target\.jpg$/.test(f.file_name) ||
                f.file_name === "[ai_generated]primary_evidence.webm"
              ) {
                names.add(f.file_name);
              }
            });

            evidenceFileNames = Array.from(names);
            console.log(
              "操作ログから抽出したエビデンスファイル:",
              evidenceFileNames
            );
          } catch (operationLogError) {
            console.error("操作ログのダウンロードに失敗:", operationLogError);
            if (!isStale()) setOperationLog(null);
            // 失敗時はパターンベースのフォールバックに切り替える
            evidenceFileNames = null;
          }
        } else {
          console.log(
            "操作ログファイルが見つかりません。ファイル名パターンベースでエビデンス取得"
          );
          if (!isStale()) setOperationLog(null);
        }

        // 操作ログ JSON の取得が終わったので「操作ログ読み込み中」表示は解除する。
        // 以降のエビデンス URL 取得は loadingEvidenceFiles で表示制御される。
        if (!isStale()) setLoadingOperationLog(false);

        // エビデンス URL 取得（並列＋プログレッシブ）。最新の load の結果だけ反映する (#433)
        if (evidenceFileNames && evidenceFileNames.length > 0) {
          await loadEvidenceFilesByNames(
            workspaceId,
            projectId,
            files,
            evidenceFileNames,
            mySeq
          );
        } else if (evidenceFileNames === null) {
          // 操作ログが無い／取得失敗 → ファイル名パターンで直接取得
          await loadEvidenceFilesDirectly(workspaceId, projectId, files, mySeq);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "操作ログの取得に失敗しました";

        // 拡張機能関連のエラーは無視
        if (
          err instanceof Error &&
          err.message.includes("message channel closed")
        ) {
          console.warn("ブラウザ拡張機能エラーを無視:", err.message);
          return;
        }

        setError(errorMessage);
        console.error("操作ログ取得エラー:", err);
      } finally {
        // 例外パスに備えて二重に false 化（成功時は上で既に false にしている）
        setLoadingOperationLog(false);
      }
    },
    [
      apiClient,
      currentTestCaseId,
      loadEvidenceFilesByNames,
      loadEvidenceFilesDirectly,
    ]
  );

  // APIクライアントが変更された時にワークスペースを自動読み込み
  useEffect(() => {
    if (apiClient) {
      loadWorkspaces();
    }
  }, [apiClient, loadWorkspaces]);

  // testTargets 取得（マスク情報 lookup 用。failSoft: 取得失敗してもマスクバッジが出ないだけ）
  useEffect(() => {
    if (!apiClient || !currentFilters.workspace || !currentFilters.project) return;
    let cancelled = false;
    setLoadingTestTargets(true);
    apiClient
      .getTestTargets(currentFilters.workspace, currentFilters.project)
      .then((result) => {
        if (!cancelled) setTestTargets(result);
      })
      .catch((err) => {
        console.warn("getTestTargets failed", err);
        if (!cancelled) setTestTargets([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTestTargets(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, currentFilters.workspace, currentFilters.project]);

  // 値だけを更新する関数
  const updateTestResultValue = useCallback(
    async (resultId: number, value: any) => {
      if (!apiClient) {
        throw new Error("APIクライアントが初期化されていません");
      }
      
      try {
        // 対応するテスト結果を見つける
        const targetResult = testResults.find(
          r => r.scriptTestRunResult.result_id === String(resultId)
        );
        
        if (!targetResult) {
          throw new Error("テスト結果が見つかりません");
        }
        
        // updateTestResultsを使用して更新
        await apiClient.updateTestResults({
          workspaceId: targetResult.testCaseDetail.workspace_id,
          projectId: targetResult.testCaseDetail.project_id,
          testRunId: targetResult.testRunId,
          testResultInputs: [{
            resultId: targetResult.scriptTestRunResult.result_id,
            scriptTestRunMatrixId: targetResult.scriptTestRunResult.script_test_run_matrix_id,
            testResultStatusId: targetResult.scriptTestRunResult.test_result_status_id,
            value: JSON.stringify(value) // JSONとして文字列化
          }]
        });
        
        // ローカルのテスト結果を更新
        setTestResults((prevResults) =>
          prevResults.map((result) => {
            if (result.scriptTestRunResult.result_id === String(resultId)) {
              return {
                ...result,
                scriptTestRunResult: {
                  ...result.scriptTestRunResult,
                  value: value,
                },
              };
            }
            return result;
          })
        );
      } catch (error) {
        console.error("テスト結果値の更新に失敗しました:", error);
        throw error;
      }
    },
    [apiClient, testResults]
  );

  // テスト結果を切り替えた瞬間に、前結果のエビデンス／操作ログ／ファイルメタを
  // 即時クリアする (#433)。これをしないと、新しい結果のファイルがロードされる前
  // （または managed_file_list が空でロード自体が走らない結果）に、前結果の動画が
  // そのまま残って表示されてしまう。進行中の load も連番更新で無効化する。
  const resetSelectedEvidence = useCallback(() => {
    loadSeqRef.current += 1;
    selectionSeqRef.current += 1;
    setSelectedResultFiles([]);
    setOperationLog(null);
    setEvidenceFiles({});
    setCurrentTestCaseId(null);
    setLoadingOperationLog(false);
    setLoadingEvidenceFiles(false);
  }, []);

  return {
    testResults,
    workspaces,
    projects,
    testRuns,
    testTargets,
    selectedResultFiles,
    operationLog,
    evidenceFiles,
    projectStatusIds,
    testStepStatusIds,
    stats,
    loading,
    loadingFiles,
    loadingOperationLog,
    loadingEvidenceFiles,
    loadingTemplate,
    loadingTestTargets,
    updatingTestResult,
    error,
    searchTestResults,
    loadWorkspaces,
    loadProjects,
    loadTestRuns,
    loadProjectTemplate,
    loadFileMetadata,
    loadOperationLogAndEvidences,
    resetSelectedEvidence,
    markAsProcessed,
    clearError,
    updateTestResult,
    updateTestResultValue,
    filteredResults,
    applyFilters,
    template,
    testStepTemplate,
    fetchSingleResult,
  };
}
